/* eslint-disable max-lines */
import 'react-datepicker/dist/react-datepicker.css';
import DatePicker, { ReactDatePickerCustomHeaderProps } from 'react-datepicker';
import React, { forwardRef, useEffect, useRef, useState, useCallback, useMemo, ReactNode } from 'react';
import { useUpdateEffect } from 'react-use';

// Local imports
import { Props, PickerMode } from './types';
import { DEFAULT_YEAR_RANGE } from './constants';
import { Relative, StyledInputWrapper, StyleWrapper } from './styled';
import CustomHeader from './CustomHeader';
import { createFeedbackLabelStyle, generateYearsArray } from './utils';
import { createPortal } from 'react-dom';

const Datepicker = forwardRef<DatePicker, Props<boolean>>(
  (
    {
      selected,
      selectedTo,
      portal = false,
      inputWidth = '200px',
      height,
      isRange = false,
      hasFeedback,
      feedbackMessage,
      feedbackColor,
      small,
      disabled,
      showCustomHeader = true,
      showQuickRangeButtons = false,
      quickRangeType = 'default',
      yearRange = DEFAULT_YEAR_RANGE,
      initialPickerMode = 'date',
      onChange,
      ...props
    },
    ref,
  ) => {
    // State
    const [startDate, setStartDate] = useState(selected ?? null);
    const [endDate, setEndDate] = useState(selectedTo ?? null);
    const [pickerMode, setPickerMode] = useState<PickerMode>(initialPickerMode);
    const [viewDate, setViewDate] = useState<Date | undefined>(selected ?? undefined);

    // Refs
    const innerRef = useRef<DatePicker>(null);

    // Effects
    useUpdateEffect(() => {
      if (innerRef.current) {
        if (props.focus) {
          innerRef.current.setFocus();
        } else {
          innerRef.current.setOpen(false);
        }
      }
    }, [props.focus]);

    useEffect(() => {
      setStartDate(selected ?? null);
      setEndDate(selectedTo ?? null);
    }, [selected, selectedTo]);

    // Memoized Values
    const feedbackLabelStyle = useMemo(() => createFeedbackLabelStyle(feedbackColor), [feedbackColor]);

    const years = useMemo(() => generateYearsArray(yearRange), [yearRange]);

    // DatePicker props based on picker mode
    const datePickerProps = useMemo(() => {
      const baseProps = {
        showMonthYearPicker: false,
        showYearPicker: false,
        showMonthDropdown: false,
        showYearDropdown: false,
        minDate: yearRange ? new Date(yearRange.start, 0, 1) : undefined,
        maxDate: yearRange ? new Date(yearRange.end, 11, 31) : undefined,
      };

      switch (pickerMode) {
        case 'year':
          return {
            ...baseProps,
            showYearPicker: true,
            minDate: yearRange ? new Date(yearRange.start, 0, 1) : undefined,
            maxDate: yearRange ? new Date(yearRange.end, 11, 31) : undefined,
          };
        case 'month':
          return {
            ...baseProps,
            showMonthYearPicker: true,
          };
        case 'date':
        default:
          return baseProps;
      }
    }, [pickerMode, yearRange]);

    // Event Handlers
    const handleDateChange = useCallback(
      (value: Date | null | [Date | null, Date | null]) => {
        // Auto-advance picker mode based on current selection
        switch (pickerMode) {
          case 'year':
            if (value) {
              setViewDate(!Array.isArray(value) ? value : value?.[0] ?? undefined);
            }
            setPickerMode('month');
            break;
          case 'month':
            if (value) {
              setViewDate(!Array.isArray(value) ? value : value?.[0] ?? undefined);
            }
            setPickerMode('date');
            break;
          case 'date':
            if (isRange && Array.isArray(value)) {
              const [start, end] = value;
              onChange(value);
              setStartDate(start);
              setEndDate(end);
            } else {
              onChange(value);
              setStartDate(value as Date | null);
            }
            break;
        }
      },
      [isRange, onChange, pickerMode],
    );

    // const handleCloseOnScroll = useCallback((e: Event) => {
    //   const target = e.target as Element;
    //   const isScrollingInHeader = target.closest('#custom-datepicker-header') !== null;
    //   return !isScrollingInHeader;
    // }, []);

    const handlePickerModeChange = useCallback((mode: PickerMode) => {
      setPickerMode(mode);
    }, []);

    // Custom Header Component
    const renderCustomHeader = useCallback(
      (headerProps: ReactDatePickerCustomHeaderProps) => (
        <CustomHeader
          {...headerProps}
          years={years}
          pickerMode={pickerMode}
          onPickerModeChange={handlePickerModeChange}
          startDate={startDate}
          endDate={endDate}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
          isRange={isRange}
          onChange={onChange}
          showQuickRangeButtons={showQuickRangeButtons}
          quickRangeType={quickRangeType}
          setViewDate={setViewDate}
        />
      ),
      [
        years,
        pickerMode,
        handlePickerModeChange,
        startDate,
        endDate,
        setStartDate,
        setEndDate,
        isRange,
        onChange,
        showQuickRangeButtons,
        quickRangeType,
      ],
    );

    const calendarContainer =
      (portal: boolean) =>
      ({ children }: { children: ReactNode }) => {
        const el = document.getElementById('datepicker-portal');
        return portal ? (
          createPortal(
            <StyleWrapper hasQuickRangeButtons={showQuickRangeButtons}>{children}</StyleWrapper>,
            el || document.body,
          )
        ) : (
          <StyleWrapper hasQuickRangeButtons={showQuickRangeButtons}>{children}</StyleWrapper>
        );
      };
    const [isOpen, setIsOpen] = useState(false);
    const handleCalendarClose = () => {
      setIsOpen(false);
    };
    const handleCalendarOpen = () => {
      setIsOpen(true);
      setViewDate(startDate || new Date());
    };

    return (
      <Relative>
        <StyledInputWrapper
          feedbackColor={feedbackColor}
          small={small}
          isOpen={isOpen}
          inputWidth={inputWidth}
          height={height}
          noBorder={props.noBorder}
          hasFeedback={hasFeedback}
        >
          <DatePicker
            ref={ref ?? innerRef}
            {...props}
            {...datePickerProps}
            startDate={startDate}
            endDate={endDate}
            openToDate={viewDate}
            onCalendarClose={handleCalendarClose}
            onCalendarOpen={handleCalendarOpen}
            popperContainer={calendarContainer(portal)}
            showPopperArrow={false}
            selected={isRange && pickerMode === 'date' ? endDate : startDate}
            disabled={disabled}
            isClearable={props.isClearable ?? disabled !== true}
            strictParsing
            renderCustomHeader={showCustomHeader ? renderCustomHeader : undefined}
            onChange={handleDateChange}
            selectsRange={isRange && pickerMode === 'date'}
            shouldCloseOnSelect={pickerMode === 'date'}
            formatWeekDay={(nameOfDay) => {
              const custom: Record<string, string> = {
                Monday: 'M',
                Tuesday: 'T',
                Wednesday: 'W',
                Thursday: 'T',
                Friday: 'F',
                Saturday: 'S',
                Sunday: 'S',
              };
              return custom[nameOfDay];
            }}
          />
          {hasFeedback && feedbackMessage && <p style={feedbackLabelStyle}>{feedbackMessage}</p>}
        </StyledInputWrapper>
      </Relative>
    );
  },
);

Datepicker.displayName = 'Datepicker';

export default Datepicker as <WithRange extends boolean | undefined = undefined>(
  props: Props<WithRange> & { ref?: React.Ref<DatePicker> },
) => React.ReactElement;
